import React, { FC, useEffect } from 'react';

import { SeverityStatusInput } from '@ariksa/compliance-policies';
import { NativeResources } from '@ariksa/compliance-policies/api';
import { Box } from '@chakra-ui/react';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';

import { selectCompliance } from 'app/containers/Compliance/selectors';
import { actions } from 'app/containers/Compliance/slice';
import { StackedHeader } from 'components/DataDisplay';
import { Drawer } from 'components/Overlay';
import { ControlTable } from 'containers/Compliance/Components/ComplianceTables/ComplianceControlTable/ControlTable';
import { PoliciesTable } from 'containers/Compliance/Components/ComplianceTables/PoliciesTable';
import { ResourceTable } from 'containers/Compliance/Components/ComplianceTables/ResourceTable/ResourceTable';
import { getBlueprintIcon } from 'containers/PolicyHub/utils';

interface Props {
  tab: string;
  isExpanded: boolean;
  setIsExpanded(isExpanded: boolean);
  environmentId?: string;
}

export const ComplianceTableTabs: FC<Props> = props => {
  const { tab, isExpanded, setIsExpanded, environmentId } = props;
  const {
    rules,
    controlStandardRules: standardRules,
    byResourceType,
    selectedBlueprint,
    resourceTypeSeverity,
    resourceType,
  } = useSelector(selectCompliance);

  const dispatch = useDispatch();

  // Api call for Control
  useEffect(() => {
    if (!isEmpty(selectedBlueprint)) {
      if (!!selectedBlueprint?.compliance) {
        dispatch(
          actions.getControlStandardRules({
            q: {
              blueprintId: selectedBlueprint?.id,
              page: standardRules.page.info.page_number,
              size: standardRules.page.info.page_size,
              level: 'control',
              environmentId,
            },
          }),
        );
      }
    }
  }, [dispatch, selectedBlueprint, standardRules.page.info, environmentId]);

  // Api call for Rules
  useEffect(() => {
    if (!isEmpty(selectedBlueprint)) {
      if (!selectedBlueprint?.compliance) {
        dispatch(
          actions.loadRules({
            q: {
              blueprintId: selectedBlueprint?.id,
              page: rules.page.info.page_number,
              size: rules.page.info.page_size,
              environmentId,
              resourceType: resourceType as NativeResources,
              severity: resourceTypeSeverity?.toUpperCase() as SeverityStatusInput,
            },
          }),
        );
      }
    }
  }, [
    dispatch,
    selectedBlueprint,
    rules.page.info,
    environmentId,
    resourceType,
    resourceTypeSeverity,
  ]);

  // Api call for compliance by resource type
  useEffect(() => {
    if (!isEmpty(selectedBlueprint)) {
      const severity = !!resourceTypeSeverity
        ? {
            severity: resourceTypeSeverity.toUpperCase() as SeverityStatusInput,
          }
        : {};
      dispatch(
        actions.getComplianceSummaryByResourceType({
          q: {
            blueprintId: selectedBlueprint?.id,
            page: byResourceType.page.info.page_number,
            size: byResourceType.page.info.page_size,
            environmentId,
            nativeResource: resourceType as NativeResources,
            ...severity,
          },
        }),
      );
    }
  }, [
    dispatch,
    selectedBlueprint,
    byResourceType.page.info,
    environmentId,
    resourceTypeSeverity,
    resourceType,
  ]);

  const renderTabs = () => {
    if (tab === 'policies') {
      if (selectedBlueprint?.compliance) return <ControlTable />;
      else return <PoliciesTable />;
    }
    return <ResourceTable />;
  };

  return isExpanded ? (
    <Drawer
      isOpen={isExpanded}
      onClose={() => setIsExpanded(false)}
      body={renderTabs()}
      header={
        <StackedHeader
          upper={selectedBlueprint?.name}
          lower={''}
          icon={getBlueprintIcon(
            selectedBlueprint?.created_by === 'system',
            selectedBlueprint?.compliance ?? selectedBlueprint?.name,
          )}
        />
      }
      closeButton
      styles={{
        content: { maxW: '1520px' },
      }}
    />
  ) : (
    <Box h="full">{renderTabs()}</Box>
  );
};
